import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';
import interfaces from '@/router/interfaces';

Vue.use(VueRouter);

const checkAvailable = ((route) => {
  const { meta } = route;

  return (!meta.getAvailability || meta.getAvailability(store.getters));
});

const redirectIfNotAuthorized = (to, from, next, p) => {
  const { $log } = Vue.prototype;
  $log.debug('Check Auth', `${p} == "${from.path}" -> "${to.path}"`);

  if (!store.getters.user) {
    $log.debug('Redirect - Empty user');
    const { name, path, query } = to;
    store.commit('setAuthRedirectLocation', { name, path, query });
    next({ name: 'SignIn', replace: true });
    return;
  }

  const { authLocation } = store.getters;
  if (authLocation) {
    store.commit('clearAuthRedirectLocation');
    // eslint-disable-next-line no-use-before-define
    const isAvailable = router.resolve(authLocation).route.matched
      .reduce((res, route) => res && checkAvailable(route), true);
    if (isAvailable) {
      $log.debug('Redirect - authLocation');
      next({ ...authLocation, replace: true });
    } else {
      $log.debug('Redirect - authLocation - Forbidden');
      next({ name: 'Forbidden' });
    }
    return;
  }

  $log.debug('Redirect - Next');
  next();
};

const routes = [
  {
    path: '/',
    exact: true,
    component: () => import('@/components/app/AppLayout'),
    beforeEnter: (to, from, next) => redirectIfNotAuthorized(to, from, next, '/'),
    children: [{
      path: '',
      redirect: 'desktop',
    }, {
      path: 'desktop',
      name: 'MainPage',
      component: () => import('@/views/MainPage'),
    }, {
      path: 'search',
      name: 'PartsSearch',
      component: () => import('@/views/orders/PartsSearch'),
    }, {
      path: 'cart',
      name: 'Cart',
      component: () => import('@/views/orders/Cart'),
    }, {
      path: 'users',
      name: 'UserList',
      component: () => import('@/views/UserList'),
    }, {
      path: 'dealers',
      name: 'DealerList',
      component: () => import('@/views/dealers/DealerList'),
    }, {
      path: 'dealer/:asdb?',
      name: 'DealerItem',
      component: () => import('@/views/dealers/DealerItem'),
      props: true,
    }, {
      path: 'logs',
      name: 'LogList',
      component: () => import('@/views/LogList'),
    }, {
      path: 'queues-list',
      name: 'QueuesList',
      component: () => import('@/views/QueuesList'),
    }, {
      path: 'versions',
      name: 'VersionList',
      component: () => import('@/views/versions/VersionList'),
    }, {
      path: 'emails/list',
      name: 'EmailList',
      component: () => import('@/views/emails/EmailList'),
    }, {
      path: 'emails/list/:id',
      name: 'Email',
      props: true,
      component: () => import('@/views/emails/Email'),
    }, {
      path: 'emails/templates',
      name: 'TemplateList',
      component: () => import('@/views/emails/TemplateList'),
    }, {
      path: 'emails/templates/:name',
      name: 'EmailTemplate',
      props: true,
      component: () => import('@/views/emails/EmailTemplate'),
    }, {
      path: 'orders',
      name: 'OrderList',
      component: () => import('@/views/OrderList'),
    }, {
      path: 'orders/:id',
      name: 'OrderItem',
      props: true,
      component: () => import('@/views/OrderItem'),
    }, {
      path: 'complaints',
      name: 'ComplaintList',
      component: () => import('@/views/ComplaintList'),
    },
    {
      path: 'complaints/:id',
      name: 'ComplaintItem',
      props: true,
      component: () => import('@/views/ComplaintItem'),
    }, {
      path: 'requests',
      name: 'RequestList',
      component: () => import('@/views/RequestList'),
    },
    {
      path: 'requests/:id',
      name: 'RequestItem',
      props: true,
      component: () => import('@/views/RequestItem'),
    },
    {
      path: 'reports/complaints',
      name: 'ComplaintsReport',
      component: () => import('@/views/reports/ComplaintsReport'),
    },
    {
      path: 'reports/settlement',
      name: 'SettlementReport',
      component: () => import('@/views/reports/SettlementReport'),
    },
    {
      path: 'content/list/:type',
      name: 'NewsList',
      props: true,
      component: () => import('@/views/news/NewsList'),
    },
    {
      path: 'content/page/:id/:type',
      name: 'NewsPage',
      props: true,
      component: () => import('@/views/news/NewsPage'),
    },
    {
      path: 'content/add/:type/:parentId?',
      name: 'AddNews',
      props: true,
      component: () => import('@/views/news/NewsEditor'),
    },
    {
      path: 'content/edit/:id/:type',
      name: 'EditNews',
      props: true,
      component: () => import('@/views/news/NewsEditor'),
    },
    {
      path: 'content/help',
      name: 'Help',
      props: true,
      component: () => import('@/views/Help'),
    },
    {
      path: 'forbidden',
      name: 'Forbidden',
      component: () => import('@/views/Forbidden'),
    }],
  },
  {
    path: '/sign-in',
    name: 'SignIn',
    component: () => import('@/views/SignIn'),
  },
  {
    path: '/sign-in/not-registred',
    name: 'UserNotRegistred',
    component: () => import('@/views/UserNotRegistred'),
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPassword'),
  }, {
    path: '/main/token-validate',
    name: 'EmailVerification',
    component: () => import('@/views/EmailVerification'),
    props: (route) => ({ verificationCode: route.query.token }),
  },
  {
    path: '*',
    component: () => import('@/views/NotFound'),
  },
];

routes[0].children.forEach((route) => {
  route.meta = { getAvailability: interfaces[route.path] };
  route.beforeEnter = (to, from, next) => redirectIfNotAuthorized(to, from, next, route.path);
});

const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior() {
    return {
      x: 0,
      y: 0,
    };
  },
});

router.onError((error) => {
  // App was updated. Need to be reloaded
  if (error.name === 'ChunkLoadError') {
    router.go(0);
    return;
  }

  // All other errors should be logged
  throw error;
});

export default router;
